import { Heading } from '@chakra-ui/react';
import Link from 'next/link';
import { parseCookies } from 'nookies';
import React, { ReactElement, useEffect, useState } from 'react';

import { COOKIE_NAME_DEALERSHIP_ID } from '@/constants/dealership/dealership.constants';
import { maxTablet } from '@/constants/vars.constants';
import { useRoutesConfig } from '@/hooks/routes-config';
import { useWindowSize } from '@/hooks/window-size.hook';
import { getAllAvailableSpecials } from '@/services/SpecialsService.service';
import { SpecialsReq } from '@/types/request/request.types';
import { SpecialFor, VehicleType } from '@/types/used-cars/used-cars.types';
import { Filter } from '@/types/utils/directus.types';

import { BlockWrapper } from '../block-wrapper/block-wrapper';
import { LinkButton } from '../buttons/link-button/link-button';
import { CarSlider } from '../details-car/car-slider/car-slider';
import { DealItem } from '../specials-category/deal-item/deal-item';

import styles from './car-specials.module.scss';

export const CarSpecials: React.FC<{
  description: string;
  descriptionDemo: string;
  carType: VehicleType;
}> = ({ description, descriptionDemo, carType }) => {
  const routes = useRoutesConfig();
  const { width } = useWindowSize();

  const isTablet = width ? width <= maxTablet : false;

  const [currentSpecials, setCurrentSpecials] = useState<SpecialsReq[]>([]);

  const specialVehicles = currentSpecials?.map((special, index) => (
    <DealItem key={index} dealInfo={special} />
  ));

  useEffect(() => {
    const getSpecials = async () => {
      const filter: Filter = {
        start_date: { _lte: '$NOW' },
        end_date: { _gte: '$NOW' },
        status: { _eq: 'approved' },
        special_for: { _eq: SpecialFor.Vehicle },
        vehicle_type: {
          _in: [VehicleType.Demo, VehicleType.Used, VehicleType.Konfidence],
        },
      };

      if (carType) {
        filter.vehicle_type = {
          _eq: carType,
        };
      }

      const cookies = parseCookies();

      if (cookies[COOKIE_NAME_DEALERSHIP_ID]) {
        filter.dealership_id = { _eq: cookies[COOKIE_NAME_DEALERSHIP_ID] };
      }

      const specials = await getAllAvailableSpecials(filter);

      setCurrentSpecials(specials);
    };

    getSpecials().catch((error) => console.error(error));
  }, [carType]);

  const getHeaderForSpecialSection = (
    activeCarType: string,
  ): string | ReactElement => {
    switch (activeCarType) {
      case VehicleType.Demo:
        return 'Demo Used Car Specials';
      default:
        return (
          <a className={styles.a_link} href={routes.specials.path}>
            Used Car Promotions
          </a>
        );
    }
  };

  const getDescriptionForSpecialSection = (
    activeCarType: string,
  ): ReactElement => {
    const getDescription = (description: string) => {
      return (
        <div
          className={styles.text}
          dangerouslySetInnerHTML={{ __html: description }}
        ></div>
      );
    };

    switch (activeCarType) {
      case VehicleType.Demo:
        return getDescription(descriptionDemo);
      default:
        return getDescription(description);
    }
  };

  if (!specialVehicles || !specialVehicles.length) {
    return;
  }
  return (
    <div className={styles.wrapper}>
      <BlockWrapper>
        <div className={styles.sectionWrapper}>
          <div className={styles.titleWrap}>
            <Heading className={styles.title} fontFamily="KiaSignature" as="h2">
              {carType && getHeaderForSpecialSection(carType)}
            </Heading>
            {isTablet ? (
              <Link href={routes.usedCars.path} className={styles.tabletLink}>
                View All
              </Link>
            ) : null}
          </div>
          <div className={styles.specialsDescription}>
            {carType && getDescriptionForSpecialSection(carType)}
            {!isTablet ? (
              <div className="mt-2">
                <LinkButton
                  label="View All Kia Promotions"
                  link={routes.specials.path}
                />
              </div>
            ) : null}
          </div>
          {specialVehicles.length > 0 ? (
            <CarSlider
              vehiclesAsComponents={specialVehicles}
              infinite={false}
              hasNav={specialVehicles.length > 3}
            />
          ) : (
            <p className={styles.notAvailable}>
              Currently there are no promotions available
            </p>
          )}
        </div>
      </BlockWrapper>
    </div>
  );
};
